/**
 * Your clock. One value, the minutes since noon, runs the hands, the sky, the
 * text colour and the readout, from noon round to your peak hour.
 */

import { gsap } from "gsap";
import { useRef } from "react";

import { CLOCK_KINDS } from "../../copy";
import { Scene, Zone, useStageHeight } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import { wr } from "../../theme";
import type { You } from "../../types";
import { count, hourLabel } from "../shared";

// As long as the deck shows the card, so the stars twinkle until it moves on.
const LENGTH = 6.5;
const START = 0.7;
// How far below the stage's foot the sun sets, glow and all, even with bands below the stage.
const BELOW = 380;

const clamp = (v: number) => Math.min(1, Math.max(0, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** The video's seeded random, so the stars sit in the same places every time. */
function seeded(start: number) {
  let seed = start;
  return () => ((seed = (seed * 16807) % 2147483647) - 1) / 2147483646;
}

const starRand = seeded(26);
const STARS = Array.from({ length: 56 }, () => {
  const size = 4 + starRand() * 7;
  // How far down the sky, so the stars fill a taller stage too.
  return { size, left: Math.round(starRand() * 1060), down: starRand(), phase: starRand() * 6.28 };
});

const twinkle = (phase: number, t: number) => 0.55 + 0.45 * Math.sin(t * 3 + phase);

/**
 * The sky `m` minutes after noon (up to the next noon). The video's evening,
 * with the edges moved so every whole hour agrees with the player's `skyAt`:
 * day until 4 pm, dusk by 5, night by 7, day again by 6 am. The sun and moon
 * rise from and set to `set`, below the stage.
 */
function skyFor(m: number, set: number) {
  const hr = 12 + m / 60;
  const evening = hr < 24;
  const dark = evening ? clamp(hr - 16) : clamp(30 - hr);
  return {
    dusk: dark,
    night: evening ? clamp(hr - 18) : clamp(30 - hr),
    // Text turns white with the dusk, and back to ink with the morning.
    white: dark,
    sun: evening ? lerp(260, set, clamp((hr - 12) / 6)) : lerp(set, 260, clamp((hr - 29) / 3)),
    moon: evening ? lerp(set, 300, clamp((hr - 18.5) / 2.5)) : 300,
    // The moon fades with the night rather than setting through the text.
    moonAlpha: evening ? 1 : clamp(30 - hr),
  };
}

const ink = (white: number) =>
  white <= 0 ? wr("ink") : white >= 1 ? wr("white") : `color-mix(in srgb, ${wr("white")} ${(white * 100).toFixed(1)}%, ${wr("ink")})`;

/** "11:00 PM", `m` minutes after noon. */
function readout(m: number) {
  const minutes = 12 * 60 + Math.floor(m);
  const h = Math.floor(minutes / 60) % 24;
  return `${h % 12 || 12}:${String(minutes % 60).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}

const spoken = (hour: number) => (hour === 0 ? "midnight" : hour === 12 ? "noon" : hourLabel(hour));

export function Clock({ clock }: { clock: You["clock"] }) {
  const peak = clock.peak_hour;
  // Forward from noon, past midnight for a morning peak.
  const span = ((peak - 12 + 24) % 24) * 60;
  const spin = 2.3 * Math.min(1.3, Math.max(0.5, span / 660));
  const end = START + spin;
  const tall = useStageHeight();
  // The timeline is built before the stage has measured the screen, so it reads the set point as it paints.
  const set = useRef(tall + BELOW);
  set.current = tall + BELOW;
  const sky = skyFor(span, set.current);
  const messages = clock.by_hour[peak] ?? 0;

  const root = useTimeline((s) => {
    const [scene] = s.q(".scene");
    const [hour, minute] = s.q(".hand");
    const [dusk] = s.q(".c-dusk");
    const [night] = s.q(".c-night");
    const [sun] = s.q(".sun");
    const [moon] = s.q(".moon");
    const [dig] = s.q(".dig");
    const stars = s.q(".star");
    const ease = gsap.parseEase("power2.inOut");

    const paint = (t: number) => {
      const m = span * ease(clamp((t - START) / spin));
      const k = skyFor(m, set.current);
      hour.style.transform = `rotate(${m / 2}deg)`;
      minute.style.transform = `rotate(${m * 6}deg)`;
      dusk.style.opacity = String(k.dusk);
      night.style.opacity = String(k.night);
      sun.style.transform = `translateY(${k.sun}px)`;
      moon.style.transform = `translateY(${k.moon}px)`;
      moon.style.opacity = String(k.moonAlpha);
      stars.forEach((star, i) => (star.style.opacity = (k.night * twinkle(STARS[i].phase, t)).toFixed(3)));
      scene.style.color = ink(k.white);
      dig.textContent = readout(m);
    };

    s.rise(".c-kick, .lead", 0.35, { y: 30, stagger: 0.12 });
    s.pop(".cface", 0.45, { from: 0.4, ease: "back.out(1.8)" });
    s.tl.fromTo(dig, { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.4 }, 0.65);
    const ticks = Math.min(14, span / 60);
    // The hands speed up and slow down (power2.inOut): the clicks follow them.
    s.ticksAlong(START, spin, "power2.inOut", ticks);
    s.sound("ding", end);
    s.tl.to(dig, { scale: 1.08, transformOrigin: "0% 50%", duration: 0.14, yoyo: true, repeat: 1, ease: "power1.out" }, end);
    s.rise(".c-peak", end, { y: 0, sound: false });
    s.rise(".c-stat", end + 0.3, { y: 26 });

    const now = { t: 0 };
    paint(0);
    s.tl.to(now, { t: LENGTH, duration: LENGTH, ease: "none", onUpdate: () => paint(now.t) }, 0);
  });

  return (
    <div ref={root} className="contents">
      <Scene style={{ color: ink(sky.white) }}>
        {/* The sky is the whole stage, so it stays out of the zones. */}
        <div aria-hidden>
          <div className="sky" style={{ background: wr("sky-day") }} />
          <div className="sky c-dusk" style={{ background: wr("sky-dusk"), opacity: sky.dusk }} />
          <div className="sky c-night" style={{ background: wr("sky-night"), opacity: sky.night }} />
          {STARS.map((star, i) => (
            <div
              key={i}
              className="star"
              style={{
                left: star.left,
                top: Math.round(140 + star.down * (tall - 180)),
                width: star.size,
                height: star.size,
                opacity: Number((sky.night * twinkle(star.phase, LENGTH)).toFixed(3)),
              }}
            />
          ))}
          <div className="sun" style={{ transform: `translateY(${sky.sun}px)` }} />
          <div className="moon" style={{ transform: `translateY(${sky.moon}px)`, opacity: sky.moonAlpha }} />
        </div>
        <Zone at="top">
          <div className="kick c-kick">Your clock</div>
          <div className="lead">When you talk the most</div>
        </Zone>
        <Zone at="middle">
          <div className="cface" aria-hidden>
            {Array.from({ length: 12 }, (_, i) => (
              <div key={i} className={`tick${i % 3 === 0 ? " big" : ""}`} style={{ transform: `rotate(${i * 30}deg)` }} />
            ))}
            <div className="hand h" style={{ transform: `rotate(${span / 2}deg)` }} />
            <div className="hand m" style={{ transform: `rotate(${span * 6}deg)` }} />
            <div className="cap" />
          </div>
          <div className="kick c-peak" style={{ top: 1150 }}>
            Your peak hour
          </div>
          <div className="dig disp">{readout(span)}</div>
        </Zone>
        <Zone at="bottom">
          <div className="line c-stat" style={{ top: 1420 }}>
            <b>{count(messages)}</b> {messages === 1 ? "message" : "messages"} between {spoken(peak)} and {spoken((peak + 1) % 24)}.{" "}
            {CLOCK_KINDS[clock.kind]}.
          </div>
        </Zone>
      </Scene>
    </div>
  );
}
